// src/components/post/PostHistory.jsx - 投稿履歴表示

import React, { useState, useEffect } from 'react';
import { COLORS, SPACING, BORDER_RADIUS, TRANSITIONS } from '../../styles/constants';

const PostHistory = ({ isOpen, onClose, onSelectPost }) => {
  const [history, setHistory] = useState([]);
  const [filter, setFilter] = useState('all'); // all, generated, posted

  useEffect(() => {
    if (isOpen) {
      loadHistory();
    }
  }, [isOpen]);

  const loadHistory = () => {
    try {
      const saved = localStorage.getItem('postHistory');
      if (saved) {
        const parsed = JSON.parse(saved);
        // 新しい順にソート
        parsed.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        setHistory(parsed);
      }
    } catch (e) {
      console.error('Failed to load history:', e);
    }
  };

  const filteredHistory = history.filter(item => {
    if (filter === 'all') return true;
    if (filter === 'generated') return !item.posted;
    if (filter === 'posted') return item.posted;
    return true;
  });

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'たった今';
    if (minutes < 60) return `${minutes}分前`;
    if (hours < 24) return `${hours}時間前`;
    if (days < 7) return `${days}日前`;
    return date.toLocaleDateString('ja-JP');
  };

  const deleteItem = (index) => {
    const newHistory = [...history];
    newHistory.splice(index, 1);
    setHistory(newHistory);
    localStorage.setItem('postHistory', JSON.stringify(newHistory));
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.4)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: SPACING.md
    }}>
      <div style={{
        background: COLORS.white,
        borderRadius: BORDER_RADIUS.xl,
        maxWidth: '500px',
        width: '100%',
        maxHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {/* ヘッダー */}
        <div style={{
          padding: SPACING.lg,
          borderBottom: `1px solid ${COLORS.gray100}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <h3 style={{
            margin: 0,
            fontSize: '1.125rem',
            fontWeight: '700',
            color: COLORS.gray900,
            display: 'flex',
            alignItems: 'center',
            gap: SPACING.sm
          }}>
            <span>📋</span>
            投稿履歴
          </h3>
          <button
            onClick={onClose}
            style={{
              background: COLORS.gray100,
              border: 'none',
              cursor: 'pointer',
              color: COLORS.gray600,
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1rem'
            }}
          >
            ×
          </button>
        </div>

        {/* フィルター */}
        <div style={{
          padding: `${SPACING.sm} ${SPACING.lg}`,
          borderBottom: `1px solid ${COLORS.gray100}`,
          display: 'flex',
          gap: '0.25rem'
        }}>
          {[
            { key: 'all', label: 'すべて' },
            { key: 'generated', label: '生成のみ' },
            { key: 'posted', label: '投稿済み' }
          ].map(item => (
            <button
              key={item.key}
              onClick={() => setFilter(item.key)}
              style={{
                padding: '0.375rem 0.75rem',
                background: filter === item.key ? COLORS.primaryGradient : COLORS.gray100,
                color: filter === item.key ? COLORS.white : COLORS.gray600,
                border: 'none',
                borderRadius: BORDER_RADIUS.sm,
                cursor: 'pointer',
                fontSize: '0.8125rem',
                fontWeight: '500',
                transition: TRANSITIONS.default
              }}
            >
              {item.label}
            </button>
          ))}
        </div>

        {/* 履歴リスト */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: SPACING.md
        }}>
          {filteredHistory.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: SPACING.xl,
              color: COLORS.gray500
            }}>
              <div style={{ fontSize: '2.5rem', marginBottom: SPACING.sm, opacity: 0.5 }}>📭</div>
              <p style={{ margin: 0 }}>履歴がありません</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.sm }}>
              {filteredHistory.map((item, index) => (
                <div
                  key={index}
                  style={{
                    background: COLORS.gray50,
                    borderRadius: BORDER_RADIUS.md,
                    padding: SPACING.md,
                    border: `1px solid ${COLORS.gray200}`,
                    cursor: 'pointer',
                    transition: TRANSITIONS.default
                  }}
                  onClick={() => {
                    onSelectPost(item.content);
                    onClose();
                  }}
                >
                  {/* メタ情報 */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: SPACING.sm
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: SPACING.sm
                    }}>
                      <span style={{
                        fontSize: '0.75rem',
                        color: COLORS.gray500
                      }}>
                        {formatDate(item.timestamp)}
                      </span>
                      {item.posted && (
                        <span style={{
                          fontSize: '0.6875rem',
                          padding: '0.125rem 0.5rem',
                          background: COLORS.successLight,
                          color: COLORS.success,
                          borderRadius: '9999px',
                          fontWeight: '600'
                        }}>
                          投稿済み
                        </span>
                      )}
                      {item.platforms && item.platforms.length > 0 && (
                        <div style={{
                          display: 'flex',
                          gap: '0.25rem'
                        }}>
                          {item.platforms.includes('twitter') && (
                            <span style={{ fontSize: '0.75rem' }}>𝕏</span>
                          )}
                          {item.platforms.includes('threads') && (
                            <span style={{ fontSize: '0.75rem' }}>@</span>
                          )}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteItem(index);
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: COLORS.gray400,
                        fontSize: '0.875rem',
                        padding: '0.25rem'
                      }}
                    >
                      🗑
                    </button>
                  </div>

                  {/* コンテンツ */}
                  <div style={{
                    fontSize: '0.875rem',
                    color: COLORS.gray700,
                    lineHeight: '1.5',
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical'
                  }}>
                    {item.content}
                  </div>

                  {/* プロンプト */}
                  {item.prompt && (
                    <div style={{
                      marginTop: SPACING.sm,
                      fontSize: '0.75rem',
                      color: COLORS.gray500
                    }}>
                      テーマ: {item.prompt.slice(0, 50)}{item.prompt.length > 50 ? '...' : ''}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// 履歴に追加するヘルパー関数
export const addToHistory = (content, prompt, posted = false, platforms = []) => {
  try {
    const saved = localStorage.getItem('postHistory');
    const history = saved ? JSON.parse(saved) : [];

    history.unshift({
      content,
      prompt,
      posted,
      platforms,
      timestamp: new Date().toISOString()
    });

    // 最大100件まで保持
    if (history.length > 100) {
      history.pop();
    }

    localStorage.setItem('postHistory', JSON.stringify(history));
  } catch (e) {
    console.error('Failed to save history:', e);
  }
};

// 投稿済みに更新するヘルパー関数
export const markAsPosted = (content, platform) => {
  try {
    const saved = localStorage.getItem('postHistory');
    if (!saved) return;

    const history = JSON.parse(saved);
    const item = history.find(h => h.content === content);

    if (item) {
      item.posted = true;
      if (!item.platforms) item.platforms = [];
      if (!item.platforms.includes(platform)) {
        item.platforms.push(platform);
      }
      localStorage.setItem('postHistory', JSON.stringify(history));
    }
  } catch (e) {
    console.error('Failed to update history:', e);
  }
};

export default PostHistory;
